'use client';

/**
 * Bug Report Modal
 *
 * Comprehensive bug reporting dialog with maximum data collection:
 * - User description (title, details, steps to reproduce)
 * - Severity and category selection
 * - Optional screenshot capture
 * - Automatic system info, console logs, and app context collection
 */

import {
  BUG_CATEGORY_LABELS,
  BUG_SEVERITY_LABELS,
  BugCategory,
  BugSeverity,
} from '@ctrl-enter/types';
import { AlertCircle, Bug, Paperclip, X } from 'lucide-react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:bug-report-modal');
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBugReport } from '../hooks/use-bug-report';
import { cn } from '../utils/cn';
import { Button } from './button';
import { Dialog } from './dialog';
import { Input } from './input';
import { Select } from './select';
import { Textarea } from './textarea';
import { toast } from '../utils/toast';

type BugReportModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { submitBugReport, isSubmitting } = useBugReport();

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [severity, setSeverity] = useState<BugSeverity>('medium');
  const [category, setCategory] = useState<BugCategory>('other');
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [screenshotAttachment, setScreenshotAttachment] = useState<{
    base64Data: string;
    mimeType: string;
    filename: string;
    sizeBytes: number;
  } | null>(null);
  const [healthCheckResults, setHealthCheckResults] = useState<string | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setStepsToReproduce('');
      setExpectedBehavior('');
      setActualBehavior('');
      setSeverity('medium');
      setCategory('other');
      setScreenshotPreview(null);
      setScreenshotAttachment(null);
      setHealthCheckResults(null);
    } else {
      // Clear any pending close timeout when modal closes
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  // File attach handler
  const handleAttachScreenshot = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Invalid File', 'Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = ev => {
      const dataUrl = ev.target?.result as string;
      const base64Data = dataUrl.split(',')[1] ?? '';
      setScreenshotPreview(dataUrl);
      setScreenshotAttachment({
        base64Data,
        mimeType: file.type,
        filename: file.name,
        sizeBytes: file.size,
      });
      toast.success('Screenshot Attached', 'Screenshot will be included in the bug report');
    };
    reader.readAsDataURL(file);

    // Reset so the same file can be re-selected if removed
    e.target.value = '';
  }, []);

  // Remove screenshot
  const handleRemoveScreenshot = useCallback(() => {
    setScreenshotPreview(null);
    setScreenshotAttachment(null);
  }, []);

  // Form validation
  const isFormValid =
    title.trim().length >= 5 &&
    title.trim().length <= 200 &&
    description.trim().length >= 10 &&
    description.trim().length <= 5000;

  // Submit handler
  const handleSubmit = useCallback(async () => {
    if (!isFormValid) {
      toast.error('Invalid Form', 'Please fill in all required fields correctly');
      return;
    }

    // Build options object with only defined values (exactOptionalPropertyTypes compliance)
    const options: {
      stepsToReproduce?: string;
      expectedBehavior?: string;
      actualBehavior?: string;
      screenshotAttachment?: {
        base64Data: string;
        mimeType: string;
        filename: string;
        sizeBytes: number;
      };
    } = {};

    if (screenshotAttachment) options.screenshotAttachment = screenshotAttachment;

    const trimmedSteps = stepsToReproduce.trim();
    if (trimmedSteps) options.stepsToReproduce = trimmedSteps;

    const trimmedExpected = expectedBehavior.trim();
    if (trimmedExpected) options.expectedBehavior = trimmedExpected;

    const trimmedActual = actualBehavior.trim();
    if (trimmedActual) options.actualBehavior = trimmedActual;

    const result = await submitBugReport(
      title.trim(),
      description.trim(),
      severity,
      category,
      options
    );

    if (result.success) {
      // Show health check results if available
      if (result.healthReport) {
        const { formatHealthReport } = await import('../services/health-check-service');
        const reportText = formatHealthReport(result.healthReport);
        setHealthCheckResults(reportText);

        toast.success(
          'Bug Report Submitted with Diagnostics',
          'Network diagnostics have been included in your report.'
        );
      } else {
        toast.success('Bug Report Submitted', 'Thank you! Your report has been sent to our team.');
      }

      // Close modal after showing results (or immediately if no health report)
      closeTimeoutRef.current = setTimeout(() => onClose(), result.healthReport ? 3000 : 0);
    } else {
      toast.error('Submission Failed', result.error || 'Could not submit bug report');
    }
  }, [
    isFormValid,
    title,
    description,
    severity,
    category,
    stepsToReproduce,
    expectedBehavior,
    actualBehavior,
    screenshotAttachment,
    submitBugReport,
    onClose,
  ]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      disableClose={isSubmitting}
      aria-labelledby="bug-report-dialog-title"
      className="flex flex-col max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
    >
        <div
          className={cn(
            'flex-1 p-4 sm:p-6 overflow-y-auto glass-surface-static-bg glass-surface-border',
            'scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent'
          )}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className={cn(
              'absolute top-3 right-3 sm:top-4 sm:right-4 p-1 sm:p-1.5 rounded-xl',
              'text-muted-foreground hover:text-foreground',
              'hover:glass-surface transition-all duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            aria-label="Close dialog"
          >
            <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-red-500/10">
              <Bug className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
            </div>
            <div>
              <h2
                id="bug-report-dialog-title"
                className="text-base sm:text-lg font-semibold text-foreground"
              >
                Report a Bug
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Help us improve by reporting issues you encounter
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-3 sm:space-y-5">
            {/* Title */}
            <div className="space-y-1.5 sm:space-y-2">
              <label htmlFor="bug-title" className="text-xs sm:text-sm font-medium text-foreground">
                Bug Title <span className="text-red-500">*</span>
              </label>
              <Input
                id="bug-title"
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                disabled={isSubmitting}
                placeholder="Brief summary of the issue"
                maxLength={200}
              />
              <p className="text-[10px] sm:text-xs text-muted-foreground text-right">
                {title.length}/200
              </p>
            </div>

            {/* Severity and Category */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5 sm:space-y-2">
                <label
                  htmlFor="bug-severity"
                  className="text-xs sm:text-sm font-medium text-foreground"
                >
                  Severity <span className="text-red-500">*</span>
                </label>
                <Select
                  id="bug-severity"
                  value={severity}
                  onChange={e => setSeverity(e.target.value as BugSeverity)}
                  disabled={isSubmitting}
                >
                  {Object.entries(BUG_SEVERITY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <label
                  htmlFor="bug-category"
                  className="text-xs sm:text-sm font-medium text-foreground"
                >
                  Category <span className="text-red-500">*</span>
                </label>
                <Select
                  id="bug-category"
                  value={category}
                  onChange={e => setCategory(e.target.value as BugCategory)}
                  disabled={isSubmitting}
                >
                  {Object.entries(BUG_CATEGORY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5 sm:space-y-2">
              <label
                htmlFor="bug-description"
                className="text-xs sm:text-sm font-medium text-foreground"
              >
                Description <span className="text-red-500">*</span>
              </label>
              <Textarea
                id="bug-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                disabled={isSubmitting}
                placeholder="Detailed description of what happened..."
                className="resize-none"
                rows={4}
                maxLength={5000}
              />
              <p className="text-[10px] sm:text-xs text-muted-foreground text-right">
                {description.length}/5000
              </p>
            </div>

            {/* Steps to Reproduce */}
            <div className="space-y-1.5 sm:space-y-2">
              <label
                htmlFor="bug-steps"
                className="text-xs sm:text-sm font-medium text-foreground flex items-center gap-1"
              >
                Steps to Reproduce
                <span className="text-[10px] sm:text-xs text-muted-foreground font-normal">
                  (optional)
                </span>
              </label>
              <Textarea
                id="bug-steps"
                value={stepsToReproduce}
                onChange={e => setStepsToReproduce(e.target.value)}
                disabled={isSubmitting}
                placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                className="resize-none"
                rows={3}
                maxLength={2000}
              />
            </div>

            {/* Expected vs Actual Behavior */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5 sm:space-y-2">
                <label
                  htmlFor="bug-expected"
                  className="text-xs sm:text-sm font-medium text-foreground flex items-center gap-1"
                >
                  Expected Behavior
                  <span className="text-[10px] sm:text-xs text-muted-foreground font-normal">
                    (optional)
                  </span>
                </label>
                <Textarea
                  id="bug-expected"
                  value={expectedBehavior}
                  onChange={e => setExpectedBehavior(e.target.value)}
                  disabled={isSubmitting}
                  placeholder="What should happen..."
                  className="resize-none"
                  rows={3}
                  maxLength={1000}
                />
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <label
                  htmlFor="bug-actual"
                  className="text-xs sm:text-sm font-medium text-foreground flex items-center gap-1"
                >
                  Actual Behavior
                  <span className="text-[10px] sm:text-xs text-muted-foreground font-normal">
                    (optional)
                  </span>
                </label>
                <Textarea
                  id="bug-actual"
                  value={actualBehavior}
                  onChange={e => setActualBehavior(e.target.value)}
                  disabled={isSubmitting}
                  placeholder="What actually happens..."
                  className="resize-none"
                  rows={3}
                  maxLength={1000}
                />
              </div>
            </div>

            {/* Screenshot Section */}
            <div className="space-y-2 sm:space-y-3">
              <label className="text-xs sm:text-sm font-medium text-foreground">
                Screenshot (optional)
              </label>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAttachScreenshot}
              />

              {!screenshotPreview ? (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                  className={cn(
                    'w-full px-4 py-3 rounded-xl',
                    'glass-surface border-2 border-dashed border-border/50',
                    'hover:border-primary/50 hover:bg-primary/5',
                    'transition-all duration-200',
                    'flex items-center justify-center gap-2',
                    'text-xs sm:text-sm text-muted-foreground hover:text-foreground',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  <Paperclip className="w-4 h-4" />
                  Attach Screenshot
                </button>
              ) : (
                <div className="relative rounded-xl overflow-hidden border border-border/50">
                  <img src={screenshotPreview} alt="Screenshot preview" className="w-full" />
                  <button
                    onClick={handleRemoveScreenshot}
                    disabled={isSubmitting}
                    className={cn(
                      'absolute top-2 right-2 p-1.5 rounded-lg',
                      'bg-red-500 hover:bg-red-600',
                      'text-white',
                      'transition-all duration-200',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                    aria-label="Remove screenshot"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>

            {/* Auto-collected data notice */}
            <div className="flex items-start gap-2 p-2.5 sm:p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
              <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-[10px] sm:text-xs text-blue-500/90 leading-relaxed">
                <strong>Auto-collected data:</strong> System info, app version, console logs, and
                current app context will be automatically included to help us diagnose the issue.
                {(category === 'performance-ai-slow' ||
                  category === 'performance' ||
                  category === 'network') && (
                  <span className="block mt-1">
                    <strong>🏥 Network diagnostics will be run automatically</strong> to check if
                    the issue is related to your network connection.
                  </span>
                )}
              </p>
            </div>

            {/* Health check results (shown after submission) */}
            {healthCheckResults && (
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-xs sm:text-sm font-medium text-foreground">
                  🏥 Network Diagnostics Results
                </label>
                <pre className="p-2.5 sm:p-3 rounded-xl bg-muted/50 text-[10px] sm:text-xs font-mono text-foreground overflow-x-auto whitespace-pre-wrap">
                  {healthCheckResults}
                </pre>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormValid}
              variant="primary"
              size="md"
              className="w-full sm:flex-1 order-1 sm:order-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>Submit Report</>
              )}
            </Button>
            <Button
              onClick={onClose}
              disabled={isSubmitting}
              variant="secondary"
              size="md"
              className="w-full sm:flex-1 order-2 sm:order-1"
            >
              Cancel
            </Button>
          </div>

          {/* Validation hint */}
          {!isFormValid && title && description && (
            <p className="text-[10px] sm:text-xs text-red-500 text-center mt-2 sm:mt-3">
              Title must be 5-200 characters, description must be 10-5000 characters
            </p>
          )}
        </div>
    </Dialog>
  );
};

export default BugReportModal;
